/**
 * SysAdmin Dashboard
 * Systemadministratör-dashboard med länkar till olika funktioner
 * Endast tillgänglig för användare med SYS_ADMIN roll
 */

import { Link } from 'react-router-dom';
import { ROUTES } from '@/constants/routes';
import { useAuth } from '@/store/authStore';
import { useTranslation } from '@/store/languageStore';
import { isSysAdminUser } from '@/utils/isSysAdminUser';
import { PageBackButton } from '@/components/common/PageBackButton';
import {
  Cog6ToothIcon,
  UserGroupIcon,
  CircleStackIcon,
  CommandLineIcon,
  DocumentTextIcon,
  ClipboardDocumentListIcon,
  ExclamationTriangleIcon,
  ChartBarIcon,
  ServerIcon,
  ClockIcon,
  ArrowPathIcon,
  FunnelIcon,
  CheckBadgeIcon,
  InboxStackIcon,
  WrenchScrewdriverIcon,
  TruckIcon,
  ShieldCheckIcon,
  QueueListIcon,
  LockClosedIcon,
  KeyIcon,
} from '@heroicons/react/24/outline';

const SysAdmin = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const showSecurity = isSysAdminUser(user);

  return (
    <div className="p-4 bg-bgContainer rounded-lg h-full">
      <div className="flex items-center gap-3 mb-6">
        <PageBackButton />
        <h1 className="text-2xl font-bold text-textDefault flex items-center gap-2">
          <WrenchScrewdriverIcon className="w-6 h-6 text-primary" />
          {t('sysAdmin.title')}
        </h1>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Link
          to={ROUTES.SYSADMIN_SSO_LOG}
          className="p-6 bg-bgElevated rounded-lg border border-border hover:border-bgPrimary transition-colors"
        >
          <div className="flex items-center gap-4">
            <div className="p-3 bg-BgPrimaryLight rounded-lg">
              <KeyIcon className="w-8 h-8 text-bgPrimary" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-textDefault">{t('sysAdmin.ssoLog.title')}</h2>
              <p className="text-sm text-textSecondary mt-1">
                {t('sysAdmin.ssoLog.cardDescription')}
              </p>
            </div>
          </div>
        </Link>

        <Link
          to={ROUTES.SYSADMIN_GPS_DATA_LAG}
          className="p-6 bg-bgElevated rounded-lg border border-border hover:border-bgPrimary transition-colors"
        >
          <div className="flex items-center gap-4">
            <div className="p-3 bg-BgPrimaryLight rounded-lg">
              <ClockIcon className="w-8 h-8 text-bgPrimary" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-textDefault">{t('sysAdmin.gpsDataLag.title')}</h2>
              <p className="text-sm text-textSecondary mt-1">
                {t('sysAdmin.gpsDataLag.cardDescription')}
              </p>
            </div>
          </div>
        </Link>

        <Link
          to={ROUTES.SYSADMIN_ROLES}
          className="p-6 bg-bgElevated rounded-lg border border-border hover:border-bgPrimary transition-colors"
        >
          <div className="flex items-center gap-4">
            <div className="p-3 bg-BgPrimaryLight rounded-lg">
              <Cog6ToothIcon className="w-8 h-8 text-bgPrimary" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-textDefault">{t('admin.roles')}</h2>
              <p className="text-sm text-textSecondary mt-1">
                {t('sysAdmin.manageRoles')}
              </p>
            </div>
          </div>
        </Link>

        <Link
          to={ROUTES.ADMIN_ORGANIZATIONS}
          className="p-6 bg-bgElevated rounded-lg border border-border hover:border-bgPrimary transition-colors"
        >
          <div className="flex items-center gap-4">
            <div className="p-3 bg-BgPrimaryLight rounded-lg">
              <UserGroupIcon className="w-8 h-8 text-bgPrimary" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-textDefault">{t('admin.organizations')}</h2>
              <p className="text-sm text-textSecondary mt-1">
                {t('sysAdmin.manageOrganizations')}
              </p>
            </div>
          </div>
        </Link>

        <Link
          to={ROUTES.SYSADMIN_DATA_MIGRATION}
          className="p-6 bg-bgElevated rounded-lg border border-border hover:border-bgPrimary transition-colors"
        >
          <div className="flex items-center gap-4">
            <div className="p-3 bg-BgPrimaryLight rounded-lg">
              <CircleStackIcon className="w-8 h-8 text-bgPrimary" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-textDefault">{t('sysAdmin.dataMigration')}</h2>
              <p className="text-sm text-textSecondary mt-1">
                {t('sysAdmin.migrateData')}
              </p>
            </div>
          </div>
        </Link>

        <Link
          to={ROUTES.SYSADMIN_IMPORT_SOURCES}
          className="p-6 bg-bgElevated rounded-lg border border-border hover:border-bgPrimary transition-colors"
        >
          <div className="flex items-center gap-4">
            <div className="p-3 bg-BgPrimaryLight rounded-lg">
              <InboxStackIcon className="w-8 h-8 text-bgPrimary" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-textDefault">
                {t('sysAdmin.importSources.title') || 'Importkällor'}
              </h2>
              <p className="text-sm text-textSecondary mt-1">
                {t('sysAdmin.importSources.description') || 'Hantera mailbox/SFTP-källor för import'}
              </p>
            </div>
          </div>
        </Link>

        <Link
          to={ROUTES.SYSADMIN_DEVICE_COMMANDS}
          className="p-6 bg-bgElevated rounded-lg border border-border hover:border-bgPrimary transition-colors"
        >
          <div className="flex items-center gap-4">
            <div className="p-3 bg-BgPrimaryLight rounded-lg">
              <CommandLineIcon className="w-8 h-8 text-bgPrimary" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-textDefault">{t('sysAdmin.deviceCommands') || 'Enhetskommandon'}</h2>
              <p className="text-sm text-textSecondary mt-1">
                {t('sysAdmin.deviceCommandsDescription') || 'Skicka kommandon till enheter'}
              </p>
            </div>
          </div>
        </Link>

        <Link
          to={ROUTES.SYSADMIN_AUDIT_LOG}
          className="p-6 bg-bgElevated rounded-lg border border-border hover:border-bgPrimary transition-colors"
        >
          <div className="flex items-center gap-4">
            <div className="p-3 bg-BgPrimaryLight rounded-lg">
              <DocumentTextIcon className="w-8 h-8 text-bgPrimary" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-textDefault">{t('sysAdmin.auditLog') || 'Audit Log'}</h2>
              <p className="text-sm text-textSecondary mt-1">
                {t('sysAdmin.auditLogDescription') || 'Visa logg över skickade kommandon'}
              </p>
            </div>
          </div>
        </Link>

        <Link
          to={ROUTES.SYSADMIN_GENERAL_AUDIT_LOG}
          className="p-6 bg-bgElevated rounded-lg border border-border hover:border-bgPrimary transition-colors"
        >
          <div className="flex items-center gap-4">
            <div className="p-3 bg-BgPrimaryLight rounded-lg">
              <ClipboardDocumentListIcon className="w-8 h-8 text-bgPrimary" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-textDefault">{t('sysAdmin.generalAuditLog') || 'Ändringslogg'}</h2>
              <p className="text-sm text-textSecondary mt-1">
                {t('sysAdmin.generalAuditLogDescription') || 'Visa alla ändringar (CREATE, UPDATE, DELETE)'}
              </p>
            </div>
          </div>
        </Link>

        <Link
          to={ROUTES.SYSADMIN_ERROR_LOG}
          className="p-6 bg-bgElevated rounded-lg border border-border hover:border-bgPrimary transition-colors"
        >
          <div className="flex items-center gap-4">
            <div className="p-3 bg-BgPrimaryLight rounded-lg">
              <ExclamationTriangleIcon className="w-8 h-8 text-bgPrimary" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-textDefault">{t('sysAdmin.errorLog.title') || 'Fellogg'}</h2>
              <p className="text-sm text-textSecondary mt-1">
                {t('sysAdmin.errorLogDescription') || 'Visa alla systemfel och exceptions'}
              </p>
            </div>
          </div>
        </Link>

        <Link
          to={ROUTES.SYSADMIN_ECO_SETTINGS}
          className="p-6 bg-bgElevated rounded-lg border border-border hover:border-bgPrimary transition-colors"
        >
          <div className="flex items-center gap-4">
            <div className="p-3 bg-BgPrimaryLight rounded-lg">
              <ChartBarIcon className="w-8 h-8 text-bgPrimary" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-textDefault">{t('sysAdmin.ecoSettings.title') || 'ECO Driving'}</h2>
              <p className="text-sm text-textSecondary mt-1">
                {t('sysAdmin.ecoSettings.description') || 'Konfigurera trösklar för ECO driving-händelser'}
              </p>
            </div>
          </div>
        </Link>

        <Link
          to={ROUTES.SYSADMIN_SYSTEM_MONITOR}
          className="p-6 bg-bgElevated rounded-lg border border-border hover:border-bgPrimary transition-colors"
        >
          <div className="flex items-center gap-4">
            <div className="p-3 bg-BgPrimaryLight rounded-lg">
              <ServerIcon className="w-8 h-8 text-bgPrimary" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-textDefault">{t('sysAdmin.systemMonitor.title') || 'Systemövervakning'}</h2>
              <p className="text-sm text-textSecondary mt-1">
                {t('sysAdmin.systemMonitor.description') || 'Övervaka CPU, minne, disk, databaser och köer'}
              </p>
            </div>
          </div>
        </Link>

        <Link
          to={ROUTES.SYSADMIN_SLOW_QUERIES}
          className="p-6 bg-bgElevated rounded-lg border border-border hover:border-bgPrimary transition-colors"
        >
          <div className="flex items-center gap-4">
            <div className="p-3 bg-BgPrimaryLight rounded-lg">
              <ClockIcon className="w-8 h-8 text-bgPrimary" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-textDefault">{t('sysAdmin.slowQueries.title') || 'Slow Queries'}</h2>
              <p className="text-sm text-textSecondary mt-1">
                {t('sysAdmin.slowQueries.description') || 'Analysera långsamma SQL-frågor'}
              </p>
            </div>
          </div>
        </Link>

        <Link
          to={ROUTES.SYSADMIN_DATA_REPLAY}
          className="p-6 bg-bgElevated rounded-lg border border-border hover:border-bgPrimary transition-colors"
        >
          <div className="flex items-center gap-4">
            <div className="p-3 bg-BgPrimaryLight rounded-lg">
              <ArrowPathIcon className="w-8 h-8 text-bgPrimary" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-textDefault">{t('sysAdmin.dataReplay.title') || 'Data Replay'}</h2>
              <p className="text-sm text-textSecondary mt-1">
                {t('sysAdmin.dataReplay.description') || 'Återläs historisk GPS-data'}
              </p>
            </div>
          </div>
        </Link>

        <Link
          to={ROUTES.SYSADMIN_TRIP_SYNC}
          className="p-6 bg-bgElevated rounded-lg border border-border hover:border-bgPrimary transition-colors"
        >
          <div className="flex items-center gap-4">
            <div className="p-3 bg-BgPrimaryLight rounded-lg">
              <ArrowPathIcon className="w-8 h-8 text-bgPrimary" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-textDefault">{t('sysAdmin.tripSync.title') || 'Trip Sync'}</h2>
              <p className="text-sm text-textSecondary mt-1">
                {t('sysAdmin.tripSync.description') || 'Synk trippar MongoDB → MariaDB'}
              </p>
            </div>
          </div>
        </Link>

        <Link
          to="/admin/sysadmin/trip-validation"
          className="p-6 bg-bgElevated rounded-lg border border-border hover:border-bgPrimary transition-colors"
        >
          <div className="flex items-center gap-4">
            <div className="p-3 bg-BgPrimaryLight rounded-lg">
              <CheckBadgeIcon className="w-8 h-8 text-bgPrimary" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-textDefault">{t('sysAdmin.tripValidation.title') || 'Trip Validering'}</h2>
              <p className="text-sm text-textSecondary mt-1">
                {t('sysAdmin.tripValidation.description') || 'Validera trippar mellan MongoDB och MariaDB'}
              </p>
            </div>
          </div>
        </Link>

        <Link
          to="/admin/sysadmin/carinfo-filter-config"
          className="p-6 bg-bgElevated rounded-lg border border-border hover:border-bgPrimary transition-colors"
        >
          <div className="flex items-center gap-4">
            <div className="p-3 bg-BgPrimaryLight rounded-lg">
              <FunnelIcon className="w-8 h-8 text-bgPrimary" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-textDefault">{t('sysAdmin.carinfoFilter.title') || 'Car.info Filter'}</h2>
              <p className="text-sm text-textSecondary mt-1">
                {t('sysAdmin.carinfoFilter.description') || 'Välj vilka Car.info-fält som visas i filtret'}
              </p>
            </div>
          </div>
        </Link>

        {showSecurity && (
        <Link
          to={ROUTES.SYSADMIN_SECURITY}
          className="p-6 bg-bgElevated rounded-lg border border-border hover:border-bgPrimary transition-colors"
        >
          <div className="flex items-center gap-4">
            <div className="p-3 bg-BgPrimaryLight rounded-lg">
              <LockClosedIcon className="w-8 h-8 text-bgPrimary" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-textDefault">{t('sysAdmin.security.title')}</h2>
              <p className="text-sm text-textSecondary mt-1">
                {t('sysAdmin.security.description')}
              </p>
            </div>
          </div>
        </Link>
        )}

        <Link
          to={ROUTES.SYSADMIN_CERTIFICATE_MONITOR}
          className="p-6 bg-bgElevated rounded-lg border border-border hover:border-bgPrimary transition-colors"
        >
          <div className="flex items-center gap-4">
            <div className="p-3 bg-BgPrimaryLight rounded-lg">
              <ShieldCheckIcon className="w-8 h-8 text-bgPrimary" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-textDefault">{t('sysAdmin.certMonitor.title')}</h2>
              <p className="text-sm text-textSecondary mt-1">
                {t('sysAdmin.certMonitor.cardDescription')}
              </p>
            </div>
          </div>
        </Link>

        <Link
          to={ROUTES.SYSADMIN_DEVICE_QUEUES}
          className="p-6 bg-bgElevated rounded-lg border border-border hover:border-bgPrimary transition-colors"
        >
          <div className="flex items-center gap-4">
            <div className="p-3 bg-BgPrimaryLight rounded-lg">
              <QueueListIcon className="w-8 h-8 text-bgPrimary" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-textDefault">{t('sysAdmin.deviceQueues.title')}</h2>
              <p className="text-sm text-textSecondary mt-1">
                {t('sysAdmin.deviceQueues.cardDescription')}
              </p>
            </div>
          </div>
        </Link>

        <Link
          to={ROUTES.SYSADMIN_VEHICLE_CARINFO_SYNC}
          className="p-6 bg-bgElevated rounded-lg border border-border hover:border-bgPrimary transition-colors"
        >
          <div className="flex items-center gap-4">
            <div className="p-3 bg-BgPrimaryLight rounded-lg">
              <TruckIcon className="w-8 h-8 text-bgPrimary" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-textDefault">{t('sysAdmin.vehicleCarinfoSync.title') || 'Synka fordon från Car.info'}</h2>
              <p className="text-sm text-textSecondary mt-1">
                {t('sysAdmin.vehicleCarinfoSync.description') || 'Synka fordonsinformation från Car.info-tjänsten'}
              </p>
            </div>
          </div>
        </Link>
      </div>
    </div>
  );
};

export default SysAdmin;
